// ----------------------------------------------------------------------------
// Notifications inbox. Live items (join requests, roster responses,
// availability changes) grouped Today / This week / Earlier; each one opens its
// source. Live items come from this session's SignalR feed plus the pending
// requests list. There is no stored inbox endpoint, so older items don't
// persist across reloads (gap report). Admins also send push broadcasts here.
// ----------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@app/auth/AuthContext';
import { useLiveNotifications, type LiveNotification } from '@app/notifications/LiveNotifications';
import { notificationsApi, type BroadcastHistoryEntry, type BroadcastResponse } from '@app/api/notifications';
import { membershipsApi, plansApi } from '@app/api/organization';
import type { EnrichedMembership, ServicePlan } from '@app/types';
import { buildTemplates } from '@admin/shared/pages/notifications/templates';
import { Avatar, Banner, Button, Chip, EmptyState, Field, Input, Modal, PageHeader, Select, SkeletonRows, Textarea, cx, useToast } from '@app/components/ui';
import { relativeWhen } from '@app/lib/format';
import { logger } from '@app/utils/logger';

export const sourcePath = (n: Pick<LiveNotification, 'kind' | 'planId'>): string =>
  n.kind === 'request' ? '/admin/membership-requests' : n.kind === 'assignment' ? (n.planId ? `/admin/roster?plan=${n.planId}` : '/admin/roster') : '/admin/team-availability';

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

export function groupByWhen<T extends { at: string }>(items: T[], now = new Date()): { label: string; items: T[] }[] {
  const today = startOfDay(now).getTime();
  const week = today - 6 * 86400000;
  const groups = [
    { label: 'Today', items: [] as T[] },
    { label: 'This week', items: [] as T[] },
    { label: 'Earlier', items: [] as T[] },
  ];
  for (const i of items) {
    const t = new Date(i.at).getTime();
    groups[t >= today ? 0 : t >= week ? 1 : 2].items.push(i);
  }
  return groups.filter((g) => g.items.length);
}

const describe = (n: LiveNotification) => (n.kind === 'request' ? 'asked to join your church' : n.what);
const audienceLabel = (a: BroadcastHistoryEntry['audience']) => (a === 'members' ? 'Selected people' : 'Whole church');

export const Notifications: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const live = useLiveNotifications();
  const churchId = user?.churchId ?? undefined;
  const [history, setHistory] = useState<BroadcastHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);

  const loadHistory = useCallback(async () => {
    if (!churchId) {
      setHistoryLoading(false);
      return;
    }
    setHistoryLoading(true);
    try {
      setHistory(await notificationsApi.getHistory(churchId, 20));
    } catch (err) {
      logger.error('Failed to load broadcast history', err);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [churchId]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  const groups = useMemo(() => groupByWhen(live.items), [live.items]);

  const open = (n: LiveNotification) => {
    live.markRead(n.id);
    navigate(sourcePath(n));
  };

  return (
    <div className="max-w-[760px]">
      <PageHeader
        eyebrow="Inbox"
        title="Notifications"
        actions={
          <>
            <Button variant="ghost" size="sm" className="!h-[38px]" onClick={live.markAllRead} disabled={!live.hasUnread}>
              Mark all read
            </Button>
            <Button variant="dark" size="sm" className="!h-[38px]" onClick={() => setComposeOpen(true)}>
              New broadcast
            </Button>
          </>
        }
      />

      {groups.length === 0 ? (
        <EmptyState title="You're all caught up" body="Join requests, roster responses and availability changes show up here as they happen." />
      ) : (
        groups.map((g) => (
          <section key={g.label} aria-label={g.label}>
            <h2 className="wp-eyebrow mt-1.5 mb-2.5">{g.label}</h2>
            <ul className="rounded-[22px] border border-line bg-card overflow-hidden mb-[22px]">
              {g.items.map((n) => (
                <li key={n.id} className="border-b border-line last:border-0">
                  <button
                    type="button"
                    onClick={() => open(n)}
                    className={cx('flex gap-3 items-start w-full text-left px-[18px] py-3.5 hover:bg-fill', !n.read && 'bg-fill')}
                  >
                    <Avatar name={n.who} size={36} />
                    <span className="flex-1 text-[14px] leading-[1.45] text-t2">
                      <span className="font-semibold text-t1">{n.who}</span> {describe(n)}
                      <span className="block text-[12px] text-t4 mt-0.5">{relativeWhen(n.at)}</span>
                    </span>
                    {!n.read && (
                      <span className="w-2 h-2 rounded-full bg-acc mt-1.5 shrink-0">
                        <span className="sr-only">Unread</span>
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}

      <h2 className="wp-eyebrow mt-2 mb-2.5">Sent broadcasts</h2>
      {historyLoading ? (
        <SkeletonRows rows={2} />
      ) : history.length === 0 ? (
        <div className="p-[26px] text-center text-t4 text-[14px] rounded-[18px] bg-fill">No broadcasts yet.</div>
      ) : (
        <ul className="rounded-[22px] border border-line bg-card overflow-hidden">
          {history.map((h) => (
            <li key={h.id} className="px-[18px] py-3.5 border-b border-line last:border-0">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[14px] font-semibold truncate">{h.title}</span>
                <span className="text-[12px] text-t4 shrink-0">{relativeWhen(h.sentAt)}</span>
              </div>
              <p className="text-[13px] text-t3 mt-0.5 line-clamp-2">{h.body}</p>
              <div className="text-[12px] text-t4 mt-1">
                {audienceLabel(h.audience)} · {h.peopleCount} {h.peopleCount === 1 ? 'person' : 'people'} · {h.deviceCount} device{h.deviceCount === 1 ? '' : 's'}
                {h.sentByName ? ` · by ${h.sentByName}` : ''}
              </div>
            </li>
          ))}
        </ul>
      )}

      <BroadcastModal open={composeOpen} onClose={() => setComposeOpen(false)} onSent={loadHistory} />
    </div>
  );
};

type Audience = 'church' | 'members';

function BroadcastModal({ open, onClose, onSent }: { open: boolean; onClose: () => void; onSent: () => void }) {
  const { user } = useAuth();
  const toast = useToast();
  const churchId = user?.churchId ?? undefined;
  const [step, setStep] = useState<'compose' | 'review'>('compose');
  const [audience, setAudience] = useState<Audience>('church');
  const [picked, setPicked] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState<string | undefined>();
  const [templateId, setTemplateId] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [dry, setDry] = useState<BroadcastResponse | null>(null);
  const [roster, setRoster] = useState<EnrichedMembership[]>([]);
  const [upcoming, setUpcoming] = useState<ServicePlan | null>(null);

  useEffect(() => {
    if (!open) {
      setStep('compose');
      setAudience('church');
      setPicked([]);
      setTitle('');
      setBody('');
      setUrl(undefined);
      setTemplateId('');
      setError('');
      setDry(null);
      return;
    }
    if (!churchId) return;
    membershipsApi.getChurchMembers(churchId).then(setRoster).catch(() => undefined);
    plansApi.getUpcoming(churchId).then(setUpcoming).catch(() => undefined);
  }, [open, churchId]);

  const templates = useMemo(() => buildTemplates({ upcoming, roster }), [upcoming, roster]);
  const people = useMemo(
    () =>
      roster
        .filter((m) => m.isActive)
        .map((m) => ({ id: m.userId, name: m.username || m.email }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [roster],
  );

  const applyTemplate = (id: string) => {
    setTemplateId(id);
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setTitle(t.draft.title);
    setBody(t.draft.body);
    setUrl(t.draft.url);
    setAudience(t.audience === 'members' ? 'members' : 'church');
    setPicked(t.audience === 'members' ? t.memberIds ?? [] : []);
  };

  const parse = (err: any) => (err?.response?.status === 403 ? 'Only church admins can send broadcasts.' : err?.response?.data?.error || 'Something went wrong. Try again.');

  const review = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return setError('Add a title and a message');
    if (audience === 'members' && picked.length === 0) return setError('Pick at least one person');
    setError('');
    setBusy(true);
    try {
      setDry(
        await notificationsApi.broadcast({
          title: title.trim(),
          body: body.trim(),
          url,
          dryRun: true,
          churchId,
          userIds: audience === 'members' ? picked : undefined,
        }),
      );
      setStep('review');
    } catch (err) {
      logger.error('Broadcast dry run failed', err);
      setError(parse(err));
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await notificationsApi.broadcast({
        title: title.trim(),
        body: body.trim(),
        url,
        dryRun: false,
        churchId,
        userIds: audience === 'members' ? picked : undefined,
        sentByUserId: user?.id,
        sentByName: user?.username,
      });
      toast(`Sent to ${res.accepted ?? res.deviceCount} device${(res.accepted ?? res.deviceCount) === 1 ? '' : 's'}`);
      onSent();
      onClose();
    } catch (err) {
      logger.error('Broadcast send failed', err);
      setError(parse(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={step === 'compose' ? 'New broadcast' : 'Send this broadcast?'} subtitle={step === 'compose' ? 'A push notification to everyone in the audience.' : dry ? `${audience === 'church' ? 'Whole church' : `${picked.length} selected`} · ${dry.peopleCount} ${dry.peopleCount === 1 ? 'person' : 'people'} on ${dry.deviceCount} device${dry.deviceCount === 1 ? '' : 's'}` : undefined} dismissible={!busy}>
      {step === 'compose' ? (
        <form className="flex flex-col gap-4" onSubmit={review} noValidate>
          {templates.length > 0 && (
            <Field label="Start from" optional htmlFor="bc-template">
              <Select
                id="bc-template"
                value={templateId}
                onChange={applyTemplate}
                placeholder="Write your own"
                options={templates.map((t) => ({ value: t.id, label: t.cardTitle, sub: t.blurb, tag: t.source === 'auto' ? 'Suggested' : undefined }))}
              />
            </Field>
          )}
          <Field label="Audience" htmlFor="bc-audience">
            <Select<Audience>
              id="bc-audience"
              value={audience}
              onChange={setAudience}
              options={[
                { value: 'church', label: 'Whole church', sub: 'Everyone with the app installed' },
                { value: 'members', label: 'Specific people', sub: 'Pick who gets it' },
              ]}
            />
          </Field>
          {audience === 'members' && (
            <div className="flex flex-wrap gap-1.5 max-h-[160px] overflow-auto" role="group" aria-label="People">
              {people.map((p) => (
                <Chip key={p.id} on={picked.includes(p.id)} onClick={() => setPicked((x) => (x.includes(p.id) ? x.filter((i) => i !== p.id) : [...x, p.id]))}>
                  {p.name}
                </Chip>
              ))}
            </div>
          )}
          <Field label="Title" htmlFor="bc-title">
            <Input id="bc-title" value={title} maxLength={80} placeholder="Rehearsal moved" onChange={(e) => setTitle(e.target.value)} />
          </Field>
          <Field label="Message" htmlFor="bc-body" error={error}>
            <Textarea id="bc-body" rows={3} value={body} maxLength={240} placeholder="Thursday rehearsal starts at 7:30 PM this week." invalid={!!error} onChange={(e) => setBody(e.target.value)} />
          </Field>
          <div className="flex gap-2.5">
            <Button variant="ghost" shape="form" size="lg" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" shape="form" size="lg" className="flex-1" loading={busy}>
              Review
            </Button>
          </div>
        </form>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex gap-3 p-3.5 rounded-[16px] bg-fill">
            <img src="/favicon.svg" alt="" className="w-9 h-9 rounded-[9px]" />
            <div className="min-w-0">
              <div className="text-[12px] text-t4">Worship Planner · now</div>
              <div className="text-[14.5px] font-semibold mt-0.5">{title}</div>
              <div className="text-[13.5px] text-t2 mt-0.5 leading-[1.45]">{body}</div>
            </div>
          </div>
          {dry?.deviceCount === 0 && <Banner tone="amb">No one in this audience has notifications turned on yet.</Banner>}
          {error && (
            <Banner tone="acc" role="alert">
              {error}
            </Banner>
          )}
          <div className="flex gap-2.5">
            <Button variant="ghost" shape="form" size="lg" onClick={() => setStep('compose')} disabled={busy}>
              Edit
            </Button>
            <Button shape="form" size="lg" className="flex-1" loading={busy} disabled={dry?.deviceCount === 0} onClick={send}>
              Send now
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
